import React, { useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { QueryLoader, Link, Flex, Text, Button, toast } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { useComplianceCaseContext } from "../../entrypoints/ComplianceCaseContext";
import {
  useGetProfileActionInfoQuery,
  useRemoveProfileFromCaseMutation,
} from "../../graphql/supergraphHooks";
import { type ProfileType, type RequestedDocumentType } from "../../graphql/supergraphTypes";
import {
  resolvedAccountType,
  isIndividualAccountType,
  isJoinAccountType,
} from "../../utils/account_types";
import { getAllAffectedDescendantProfiles } from "../AccountTypeForm/utils";
import { ButtonWrapper } from "../common/ButtonWrapper";
import { computeChildOwnerCountByProfileId } from "../common/ProfileManagement/direct_owner_count";
import { CompletionDocumentStatus } from "../common/ProfileManagement/types";
import { sharedHeaderStyle } from "../common/style_vanilla.css";
import { DeleteProfileModal } from "../DeleteProfileModal";
import { type ProfileToDelete } from "../DeleteProfileModal/types";
import { allDocumentsUploaded } from "../documents/DocumentCollectorInvestor/utils";
import { DocumentStatusLabel } from "../documents/DocumentStatusLabel";
import { ProfileActionOverviewItem } from "../ProfileActionOverview/OverviewItem";
import { ProfileStatus } from "../ProfileStatusBadge/types";
import { completeButtonContainer } from "../ProfileTree/style_vanilla.css";
import { provideLegalName } from "../ProfileTree/utils";
import { completionText, completionTooltipText, isStandAloneCase } from "../utils";
import { deleteIcon, description } from "./style_vanilla.css";
import {
  completionStatus,
  controllerItemStatus,
  detailItemStatus,
  documentItemStatus,
  NavigationMode,
  ownerItemStatus,
  shouldShow,
} from "./utils";

export const ProfileActionOverview: React.FC<{ onStepCompletion: () => void }> = ({
  onStepCompletion,
}) => {
  const history = useHistory();
  const {
    caseId,
    basePath,
    isReadOnly,
    programId,
    skipDetailsForRootProfile,
    caseType,
    rootProfileId,
  } = useComplianceCaseContext();
  let { profileId } = useParams<{ profileId: string }>();

  const [profileToDelete, setProfileToDelete] = useState<ProfileToDelete | null>(null);

  const profileActionResult = useGetProfileActionInfoQuery({
    variables: { caseId: caseId },
    skip: !caseId,
    fetchPolicy: "no-cache",
  });

  const [removeProfileFromCase] = useRemoveProfileFromCaseMutation({
    onError: (e) => toast.error(e.message),
    onCompleted: () => {
      toast.success("Profile deleted");
      setProfileToDelete(null);
      history.push(basePath);
    },
    refetchQueries: ["GetProfileActionInfo", "GetComplianceCase", "GetProfilesForCase"],
    awaitRefetchQueries: true,
  });

  const complianceCase = profileActionResult.data?.complianceCase;
  const rootProfile = complianceCase?.investingProfile;
  profileId = profileId ?? complianceCase?.investingProfile?.id.toString();
  const parsedProfileId = parseInt(profileId, 10);
  const profile =
    complianceCase?.profiles.find((candidate) => candidate?.id === parsedProfileId) ??
    (rootProfile?.id === parsedProfileId ? rootProfile : undefined);

  const investingProfileName = provideLegalName(rootProfile);
  const profileName = provideLegalName(profile);
  const resolvedRootAccountType = resolvedAccountType(rootProfile);
  const resolvedProfileAccountType = resolvedAccountType(profile);

  const childOwnerCountByProfileId = useMemo(
    () => computeChildOwnerCountByProfileId(complianceCase?.profiles),
    [complianceCase?.profiles],
  );

  const childOwnersCount = childOwnerCountByProfileId.get(profile?.id ?? rootProfileId) ?? 0;

  const profileWithOwnerCount = profile ? { ...profile, childOwnersCount } : profile;
  const { detailsCompleted, documentsCompletionStatus, ownersCompleted, controllersCompleted } =
    completionStatus(
      caseType,
      {
        id: rootProfileId,
        accountType: resolvedRootAccountType,
        rulesEngine: rootProfile?.rulesEngine,
      },
      profileWithOwnerCount,
      programId,
    );
  const documentStarted = detailsCompleted
    ? documentsCompletionStatus == CompletionDocumentStatus.COMPLETED ||
      documentsCompletionStatus == CompletionDocumentStatus.ACKNOWLEDGED
    : false;
  const documentsCompleted = documentsCompletionStatus == CompletionDocumentStatus.COMPLETED;

  const detailsStatus = detailItemStatus(detailsCompleted);
  const documentsStatus = documentItemStatus(detailsCompleted, documentsCompletionStatus);
  const ownersStatus = ownerItemStatus(
    ownersCompleted,
    detailsCompleted,
    resolvedProfileAccountType,
    childOwnersCount,
  );
  const controllersStatus = controllerItemStatus(controllersCompleted);

  const {
    navigationMode,
    showDetails,
    showDocuments,
    showOwners,
    showControllers,
    showAsIdentificationCard,
  } = shouldShow(
    caseType,
    {
      id: rootProfileId,
      accountType: resolvedRootAccountType,
      rulesEngine: rootProfile?.rulesEngine,
      amlEntityTypeQuestions: rootProfile?.amlEntityTypeQuestions,
    },
    profile,
    programId,
    skipDetailsForRootProfile,
  );

  const navigationModeDetails = useMemo(() => {
    switch (navigationMode) {
      case NavigationMode.IraCategorization:
        return {
          path: "/ira-categorization",
          label: "Select account type",
        };
      case NavigationMode.AccountTypeSelection:
        return {
          path: "?show_account_type=true",
          label: "Account type",
        };
    }
  }, [navigationMode]);

  const onDetailsClick = () => {
    if (isIndividualAccountType(resolvedProfileAccountType)) {
      history.push(`${basePath}/edit-individual/${profileId}`);
    } else if (isJoinAccountType(resolvedProfileAccountType)) {
      history.push(`${basePath}/edit-joint/${profileId}`);
    } else {
      history.push(`${basePath}/edit-institution/${profileId}`);
    }
  };
  const onDocumentsClick = () => {
    history.push(`${basePath}/documents/${profileId}`);
  };
  const onOwnersClick = () => {
    if (isJoinAccountType(resolvedProfileAccountType)) {
      history.push(`${basePath}/owners/${profileId}`);
    } else {
      history.push(`${basePath}/owners/threshold-check/${profileId}`);
    }
  };
  const onControllersClick = () => {
    history.push(`${basePath}/controllers/${profileId}`);
  };

  const isNonRootProfile = Number(profileId) !== rootProfileId;

  const handleDeleteClick = () => {
    if (!profile) return;

    const caseProfiles = (complianceCase?.profiles ?? []) as ProfileType[];
    const descendants = getAllAffectedDescendantProfiles(caseProfiles, profileId);
    const descendantNames = descendants.map((p) => provideLegalName(p));

    setProfileToDelete({
      id: profile.id.toString(),
      name: profileName,
      descendantNames,
    });
  };

  const handleConfirmDelete = async (caseIdArg: number, profileIdArg: string) => {
    await removeProfileFromCase({
      variables: {
        caseId: caseIdArg,
        profileId: Number(profileIdArg),
      },
    });
  };

  return (
    <QueryLoader result={profileActionResult}>
      <div>
        {navigationMode && (
          <Link
            to={`${basePath}${navigationModeDetails?.path || ""}`}
            data-testid={"selection-back-link"}
            variant="muted"
            icon="arrow-left"
            iconPosition="left"
            align="left"
          >
            {navigationModeDetails?.label || investingProfileName}
          </Link>
        )}
        <Flex flexDirection="column">
          <Flex justifyContent="space-between" alignItems="center">
            <Text variant="h3" className={sharedHeaderStyle}>
              {isStandAloneCase(caseType) || !showAsIdentificationCard
                ? profileName
                : "Identification"}
            </Text>
            {!isReadOnly && isNonRootProfile && (
              <Icon
                type="trash"
                size="small"
                data-testid="delete-profile-icon"
                onClick={handleDeleteClick}
                className={deleteIcon}
              />
            )}
          </Flex>
          {(() => {
            const isIndividual = isIndividualAccountType(resolvedProfileAccountType);

            switch (true) {
              case isStandAloneCase(caseType) && isIndividual:
                return (
                  <Text variant="regular-md" className={description}>
                    Please verify the information is up to date and complete. Any modifications
                    you apply will be reflected across all associated investments.
                  </Text>
                );
              case isIndividual:
                return (
                  <Text variant="regular-md" className={description}>
                    Additional information about {profileName} is required. Entering this
                    information accurately is a regulatory requirement.
                  </Text>
                );
              default:
                return (
                  <Text variant="regular-md" className={description}>
                    Additional information about {profileName} and its subsidiaries is required.
                    Entering this information accurately is a regulatory requirement.
                  </Text>
                );
            }
          })()}
          <Flex gap="20px" flexDirection="column">
            <Flex flexDirection="column">
              {showDetails && (
                <ProfileActionOverviewItem
                  title="Details"
                  subTitle={`Basic information about this ${isIndividualAccountType(resolvedProfileAccountType) ? "individual" : "entity"}`}
                  buttonType={detailsCompleted ? "edit" : "start"}
                  buttonEnabled={!isReadOnly}
                  onClick={onDetailsClick}
                  status={detailsStatus}
                />
              )}
              {showDocuments && (
                <ProfileActionOverviewItem
                  title="Documents"
                  subTitle={
                    isIndividualAccountType(resolvedProfileAccountType)
                      ? "Identification documents and others based on entity details"
                      : "Formation documents and others based on entity details"
                  }
                  buttonType={documentStarted ? "edit" : "start"}
                  buttonEnabled={
                    showDetails ? detailsCompleted != null && detailsCompleted : true
                  }
                  buttonTooltip={
                    showDetails && !detailsCompleted
                      ? "Documents cannot be uploaded without first completing the Details section for this entity."
                      : undefined
                  }
                  onClick={onDocumentsClick}
                  status={documentsStatus}
                  warning={
                    documentStarted && (
                      <DocumentStatusLabel
                        documentTypes={
                          profile?.rulesEngine?.documentTypes as RequestedDocumentType[]
                        }
                      />
                    )
                  }
                />
              )}
              {showOwners && (
                <ProfileActionOverviewItem
                  title="Owners"
                  subTitle="Add profiles for individuals or entities with significant equity interests"
                  buttonType={ownersCompleted ? "edit" : "start"}
                  buttonEnabled={ownersStatus !== ProfileStatus.DetailsPending && !isReadOnly}
                  buttonTooltip={
                    ownersStatus === ProfileStatus.DetailsPending
                      ? "Owners cannot be updated without completing the Details section for this entity."
                      : undefined
                  }
                  onClick={onOwnersClick}
                  status={ownersStatus}
                />
              )}
              {showControllers && (
                <ProfileActionOverviewItem
                  title="Controllers"
                  subTitle="Add profiles for individuals with significant responsibility for managing the business"
                  buttonType={controllersCompleted ? "edit" : "start"}
                  buttonEnabled={!isReadOnly}
                  onClick={onControllersClick}
                  status={controllersStatus}
                />
              )}
            </Flex>
          </Flex>
        </Flex>
        {showAsIdentificationCard && (
          <Flex className={completeButtonContainer}>
            <Button
              variant="neutral"
              onClick={() => {
                history.push(`${basePath}?show_account_type=true`);
              }}
              data-testid="profile-action-back-button"
            >
              Back
            </Button>
            <ButtonWrapper
              tooltip={completionTooltipText(caseType)}
              id="help_indicator"
              disabled={detailsCompleted === false || !documentsCompleted}
            >
              <Button
                variant="primary"
                onClick={() => {
                  onStepCompletion();
                }}
                data-testid="profile-action-complete-button"
                disabled={
                  !(
                    (detailsCompleted === null || detailsCompleted === true) &&
                    documentsCompleted &&
                    allDocumentsUploaded(profile?.rulesEngine?.documentTypes || [])
                  )
                }
              >
                {completionText(caseType)}
              </Button>
            </ButtonWrapper>
          </Flex>
        )}
      </div>
      <DeleteProfileModal
        profile={profileToDelete}
        caseId={Number(caseId)}
        show={!!profileToDelete}
        rootProfileName={investingProfileName}
        onClose={() => setProfileToDelete(null)}
        onDelete={handleConfirmDelete}
      />
    </QueryLoader>
  );
};
